import { ImageWithLoader, ModalTitle } from '@overlayqa/shared/ui';
/**
 * Private sub-components for IssueDrawer.
 * Extracted to keep IssueDrawer.tsx under 400 lines.
 */
import { useState, useRef, useCallback } from 'react';
import { HugeiconsIcon } from '@hugeicons/react';
import { Maximize01Icon, CancelCircleIcon, Image01Icon, BulbIcon, StarIcon } from '@hugeicons/core-free-icons';
import { Kbd } from '../ui/Kbd';
import type { AISuggestion } from '@overlayqa/shared';
import type { SelectionRect } from './SelectionOverlay';
import { resolveAssetUrl } from '@/lib/api/client';
import { log } from '@/lib/logger';

log.info('[IssueDrawerParts] Module loaded');

// ── ScreenshotSection ─────────────────────────────────────────────────────

interface ScreenshotSectionProps {
  screenshotUrl: string | null;
  onClearScreenshot: () => void;
  selection?: SelectionRect;
}

export function ScreenshotSection({ screenshotUrl, onClearScreenshot, selection }: ScreenshotSectionProps) {
  log.info('[ScreenshotSection] Render — hasScreenshot:', { screenshotUrl: !!screenshotUrl });
  const [expanded, setExpanded] = useState(false);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const openExpand = useCallback(() => {
    setExpanded(true);
    dialogRef.current?.showModal();
  }, []);
  const closeExpand = useCallback(() => {
    dialogRef.current?.close();
    setExpanded(false);
  }, []);

  return (
    <div className="rounded-xl overflow-hidden border border-gray-200 bg-white">
      {screenshotUrl ? (
        <div className="relative group">
          <ImageWithLoader
            src={resolveAssetUrl(screenshotUrl)}
            alt="Screenshot"
            className="w-full max-h-[200px] object-contain bg-gray-50 cursor-pointer"
            onClick={openExpand}
          />
          <button
            type="button"
            onClick={openExpand}
            className="absolute top-2 left-2 p-1 bg-black/50 text-white rounded opacity-0 group-hover:opacity-100 transition-opacity"
            aria-label="Expand screenshot"
          >
            <HugeiconsIcon icon={Maximize01Icon} size={16} strokeWidth={2} color="currentColor" className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={onClearScreenshot}
            className="absolute top-2 right-2 p-1 bg-black/50 text-white rounded opacity-0 group-hover:opacity-100 transition-opacity"
            aria-label="Remove screenshot"
          >
            <HugeiconsIcon icon={CancelCircleIcon} size={16} strokeWidth={2} color="currentColor" className="w-4 h-4" />
          </button>
          {/* Screenshot lightbox */}
          <dialog
            ref={dialogRef}
        aria-label="Screenshot"
            className="fixed inset-0 w-full h-full max-w-none max-h-none m-0 p-0 bg-transparent backdrop:bg-black/80"
            onClick={(e) => { if (e.target === e.currentTarget) closeExpand(); }}
            onClose={() => setExpanded(false)}
          >
            {expanded && (
              <div className="flex items-center justify-center w-full h-full px-8 pt-16 pb-8">
            <ModalTitle icon="image" tone="on-dark" className="fixed top-4 left-4 max-w-[calc(100%_-_5rem)] rounded-md bg-black/60 px-3 py-2 text-sm text-white">Screenshot</ModalTitle>
                <button
                  type="button"
                  onClick={closeExpand}
                  className="fixed top-4 right-4 z-10 p-2 bg-black/60 hover:bg-black/80 text-white rounded-full transition-colors"
                  aria-label="Close"
                >
                  <HugeiconsIcon icon={CancelCircleIcon} size={20} strokeWidth={2} color="currentColor" className="w-5 h-5" />
                </button>
                <ImageWithLoader
                  frameStyle={{ maxWidth: '100%', height: '100%' }}
                  src={resolveAssetUrl(screenshotUrl)}
                  alt="Screenshot (expanded)"
                  className="max-w-full max-h-full object-contain rounded-lg"
                />
              </div>
            )}
          </dialog>
        </div>
      ) : (
        <div className="p-6 flex items-center justify-center min-h-[120px] bg-gray-50">
          <div className="text-center text-gray-400">
            <HugeiconsIcon icon={Image01Icon} size={40} strokeWidth={2} color="currentColor" className="w-10 h-10 mx-auto mb-2 text-gray-300" />
            <p className="text-sm font-medium text-gray-500 mb-1">Paste screenshot</p>
            <p className="text-xs text-gray-400">Press <Kbd>⌘V</Kbd> to paste</p>
          </div>
        </div>
      )}
      {selection && (
        <div className="px-3 py-2 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
          <span className="text-xs font-medium text-gray-500">Selection</span>
          <span className="text-xs text-gray-400 font-mono">
            {Math.round(selection.width)} x {Math.round(selection.height)}
          </span>
        </div>
      )}
    </div>
  );
}

// ── AiSuggestionCard ──────────────────────────────────────────────────────

interface AiSuggestionCardProps {
  suggestion: AISuggestion | null;
  isAnalyzing: boolean;
  onApply: () => void;
}

export function AiSuggestionCard({ suggestion, isAnalyzing, onApply }: AiSuggestionCardProps) {
  log.info('[AiSuggestionCard] Render', { isAnalyzing, hasSuggestion: !!suggestion });
  return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <HugeiconsIcon icon={BulbIcon} size={16} strokeWidth={2} color="currentColor" className="w-4 h-4 text-amber-600" />
          <span className="font-medium text-amber-800 text-sm">
            {isAnalyzing ? 'Analyzing...' : `AI Analysis (${suggestion?.confidence}% confidence)`}
          </span>
        </div>
        {suggestion && !isAnalyzing && (
          <button
            type="button"
            onClick={onApply}
            className="btn-primary btn-sm"
          >
            <HugeiconsIcon icon={StarIcon} size={14} strokeWidth={2} color="currentColor" className="w-3.5 h-3.5" />
            <span className="text-cap-trim">Apply suggestions</span>
          </button>
        )}
      </div>

      {suggestion && !isAnalyzing && (
        <div className="space-y-3 text-sm">
          <div>
            <span className="text-amber-700 font-medium">Title: </span>
            <span className="text-amber-900 font-medium">{suggestion.title}</span>
          </div>
          {suggestion.description && (
            <div>
              <span className="text-amber-700 font-medium">Description: </span>
              <span className="text-amber-900">{suggestion.description}</span>
            </div>
          )}
          <div className="flex flex-wrap gap-3">
            <div>
              <span className="text-amber-700 font-medium">Priority: </span>
              <span className="text-amber-900 capitalize">{suggestion.severity}</span>
            </div>
            {suggestion.estimatedHours && (
              <div>
                <span className="text-amber-700 font-medium">Est. Hours: </span>
                <span className="text-amber-900">{suggestion.estimatedHours}h</span>
              </div>
            )}
          </div>
          {suggestion.labels && suggestion.labels.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-amber-700 font-medium">Tags: </span>
              {suggestion.labels.map(label => (
                <span
                  key={label}
                  className="px-2 py-1 bg-amber-100 text-amber-800 rounded text-xs font-medium"
                >
                  {label}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {isAnalyzing && (
        <div className="flex items-center gap-2 text-amber-700">
          <div className="animate-spin h-4 w-4 border-2 border-amber-500 border-t-transparent rounded-full" />
          <span className="text-sm">Analyzing your description...</span>
        </div>
      )}
    </div>
  );
}
